<template>
  <div class="picture-upload">
    <slot></slot>
    <input type="file" accept="image/*" class="input-file" ref="file" @change="selectImage">
    <portal to="dialogs" v-if="url">
      <div class="croppie-wrapper">
        <div class="croppie-header">
          <div class="crop-text">Crop your picture</div>
          <div class="button-container">
            <button class="cancel-button" aria-label="Cancel"
            @click="url = null">Cancel</button>
            <button class="apply-button" aria-label="Apply"
            @click="upload"
              :disabled="isUploading">Apply</button>
          </div>
        </div>
        <you-paired-croppie
          v-if="url"
          :image="url"
          class="croppie-component"
          ref="croppieRef"
          :opts="{
            enableOrientation: true,
            enableResize: this.enableResize,
            showZoomer: false,
            viewport: {
              width: this.width * this.scaleForWindowSize,
              height: this.height * this.scaleForWindowSize,
              type: shape,
            },
          }">
        </you-paired-croppie>
      </div>
    </portal>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import YouPairedCroppie from './YouPairedCroppie.vue';

export default {
  name: 'ImageUploader',
  components: {
    YouPairedCroppie,
  },
  data() {
    return {
      isUploading: false,
      url: null,
      scales: [{
        width: 300,
        scale: 0.2,
      }, {
        width: 400,
        scale: 0.3,
      }, {
        width: 500,
        scale: 0.4,
      }, {
        width: 600,
        scale: 0.5,
      }, {
        width: 700,
        scale: 0.6,
      }, {
        width: 800,
        scale: 0.7,
      }, {
        width: 900,
        scale: 0.8,
      }, {
        width: 1024,
        scale: 1,
      }],
    };
  },
  props: {
    imageType: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    shape: {
      type: String,
      validator: val => ['circle', 'square'].includes(val),
    },
    uploadUrl: {
      type: String,
      default: '',
    },
    resourceId: {
      type: String,
      default: '',
    },
    enableResize: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    scaleForWindowSize() {
      const windowWidth = window.innerWidth;
      const matchingScale = this.scales.find(scale => windowWidth <= scale.width);
      return matchingScale ? matchingScale.scale : 1;
    },
  },
  methods: {
    async selectImage(event) {
      const files = event.target.files || event.dataTransfer.files;
      const file = files[0];
      // reset the file input selection to empty now that we have it in local const
      // this allows us to reselect the same file if user cancels out before applying
      this.$refs.file.value = null;
      if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          // HACK, wait a second so that SAFARI browser can load the croppie ref
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          if (isSafari) {
            const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
            await delay(1000);
          }
          this.url = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async upload() {
      this.isUploading = true;
      const fileType = 'jpeg';
      const { height, width } = this.$refs.croppieRef.croppie.options.viewport;
      const croppieResult = await this.$refs.croppieRef.upload({
        type: 'blob',
        format: fileType,
        size: {
          width: width / this.scaleForWindowSize,
          height: height / this.scaleForWindowSize,
        },
      });
      try {
        const response = await this.backendAxios.post(this.uploadUrl, {
          fileType,
          resourceId: this.resourceId,
        });
        if (!response || !response.data) {
          throw new Error('Unable to retrieve upload URL');
        }
        await axios.put(response.data.signedUrl, croppieResult, {
          headers: {
            'Content-Type': fileType,
          },
        });
        this.$emit('uploaded', response.data.cfUrl);
        this.url = null;
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
      this.isUploading = false;
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.picture-upload {
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.input-file {
  opacity: 0; /* invisible but it's there! */
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  cursor: pointer;
}

.croppie-wrapper {
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 100;
}

.croppie-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  height: 3rem;
}

.button-container {
  display: flex;
  margin-left: auto;
}

.apply-button {
  margin: 0 0.5rem;
}

.croppie-header button {
  background-color: $login-background;
  font-size: 1rem;
  border-color: $login-background;
  height: 2rem;
  border-radius: 0.3rem;
  color: #fff;
  cursor: pointer;
}

.croppie-header button[disabled=disabled], .croppie-header button:disabled {
  background-color: gray;
  border-color: gray;
}

.crop-text {
  grid-column-start: 2;
}

@media screen and (max-width: 811px) {
  .crop-text {
    font-size: 1.5rem;
  }
}

</style>
